// ============================================================
// REUSABLE UI COMPONENTS
// ============================================================

import React, { useState } from 'react';
import { Search, ChevronLeft, ChevronRight, X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { getStatusColor, getStatusBg } from '../../utils/helpers';

// ── Badge ─────────────────────────────────────────────────
export const Badge = ({ status, label, dot = true }) => {
  const text = label || status;
  const color = getStatusColor(status);
  const bg = getStatusBg(status);
  return (
    <span className="admin-badge" style={{ color, backgroundColor: bg }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />}
      {text}
    </span>
  );
};

// ── Priority Badge ────────────────────────────────────────
export const PriorityBadge = ({ priority }) => {
  const map = {
    high: { color: '#DC2626', bg: '#FEE2E2' },
    medium: { color: '#D97706', bg: '#FEF3C7' },
    low: { color: '#059669', bg: '#D1FAE5' },
  };
  const s = map[priority?.toLowerCase()] || map.low;
  return (
    <span className="admin-badge" style={{ color: s.color, backgroundColor: s.bg }}>
      {priority}
    </span>
  );
};

// ── Button ────────────────────────────────────────────────
export const Button = ({ children, variant = 'primary', size = '', onClick, disabled, icon, className = '' }) => (
  <button
    className={`admin-btn admin-btn-${variant} ${size ? `admin-btn-${size}` : ''} ${className}`}
    onClick={onClick}
    disabled={disabled}
  >
    {icon && <span style={{ display: 'flex' }}>{icon}</span>}
    {children}
  </button>
);

// ── Icon Button ───────────────────────────────────────────
export const IconButton = ({ icon, onClick, title, variant = 'secondary', size = 'sm' }) => (
  <button
    className={`admin-btn admin-btn-${variant} admin-btn-${size} admin-btn-icon`}
    onClick={onClick}
    title={title}
  >
    {icon}
  </button>
);

// ── Card ──────────────────────────────────────────────────
export const Card = ({ title, actions, children, padding = true }) => (
  <div className="admin-card">
    {title && (
      <div className="admin-card-header">
        <h3 className="admin-card-title">{title}</h3>
        {actions && <div>{actions}</div>}
      </div>
    )}
    <div className={padding ? 'admin-card-body' : ''}>{children}</div>
  </div>
);

// ── Stat Card ─────────────────────────────────────────────
export const StatCard = ({ label, value, icon, color = '#E85D04', change, changeLabel }) => (
  <div className="admin-stat-card">
    <div className="admin-stat-card-accent" style={{ background: `linear-gradient(90deg, ${color}, ${color}88)` }} />
    <div className="admin-stat-card-top">
      <div>
        <p className="admin-stat-card-label">{label}</p>
        <p className="admin-stat-card-value">{value}</p>
        {change !== undefined && (
          <div className={`admin-stat-card-change ${change >= 0 ? 'up' : 'down'}`}>
            {change >= 0 ? '↑' : '↓'} {Math.abs(change)}%
            {changeLabel && <span style={{ fontWeight: 400, color: 'var(--admin-text-muted)' }}> {changeLabel}</span>}
          </div>
        )}
      </div>
      {icon && (
        <div className="admin-stat-card-icon" style={{ background: `${color}18` }}>
          <span style={{ color }}>{icon}</span>
        </div>
      )}
    </div>
  </div>
);

// ── Search Input ──────────────────────────────────────────
export const SearchInput = ({ value, onChange, placeholder = 'Search...' }) => (
  <div className="admin-table-search">
    <Search size={14} className="admin-table-search-icon" />
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </div>
);

// ── Select ────────────────────────────────────────────────
export const Select = ({ value, onChange, options, placeholder }) => (
  <select
    className="admin-form-control admin-form-select"
    value={value}
    onChange={e => onChange(e.target.value)}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </select>
);

// ── Table ─────────────────────────────────────────────────
export const Table = ({ columns, data, loading, emptyMessage = 'No data found' }) => {
  if (loading) return <SkeletonTable rows={5} cols={columns.length} />;
  return (
    <div className="admin-table-container">
      <table className="admin-table">
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>
                <EmptyState message={emptyMessage} />
              </td>
            </tr>
          ) : (
            data.map((row, idx) => (
              <tr key={row.id || idx}>
                {columns.map(col => (
                  <td key={col.key}>
                    {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// ── Pagination ────────────────────────────────────────────
export const Pagination = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange }) => {
  const start = (currentPage - 1) * itemsPerPage + 1;
  const end = Math.min(currentPage * itemsPerPage, totalItems);
  const pages = Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
    if (totalPages <= 5) return i + 1;
    if (currentPage <= 3) return i + 1;
    if (currentPage >= totalPages - 2) return totalPages - 4 + i;
    return currentPage - 2 + i;
  });
  return (
    <div className="admin-pagination">
      <span className="admin-pagination-info">
        Showing <b>{start}–{end}</b> of <b>{totalItems}</b> results
      </span>
      <div className="admin-pagination-controls">
        <button className="admin-pagination-btn" onClick={() => onPageChange(1)} disabled={currentPage === 1}>«</button>
        <button className="admin-pagination-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
          <ChevronLeft size={14} />
        </button>
        {pages.map(p => (
          <button
            key={p}
            className={`admin-pagination-btn ${p === currentPage ? 'active' : ''}`}
            onClick={() => onPageChange(p)}
          >{p}</button>
        ))}
        <button className="admin-pagination-btn" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
          <ChevronRight size={14} />
        </button>
        <button className="admin-pagination-btn" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages}>»</button>
      </div>
    </div>
  );
};

// ── Modal ─────────────────────────────────────────────────
export const Modal = ({ open, onClose, title, children, footer, size = 'md' }) => {
  if (!open) return null;
  const maxWidths = { sm: 400, md: 520, lg: 720, xl: 900 };
  return (
    <div className="admin-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="admin-modal" style={{ maxWidth: maxWidths[size] }}>
        <div className="admin-modal-header">
          <h3 className="admin-modal-title">{title}</h3>
          <button className="admin-modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="admin-modal-body">{children}</div>
        {footer && <div className="admin-modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

// ── Confirm Modal ─────────────────────────────────────────
export const ConfirmModal = ({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', variant = 'danger' }) => (
  <Modal
    open={open}
    onClose={onClose}
    title={title}
    footer={
      <>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant={variant} onClick={() => { onConfirm(); onClose(); }}>{confirmLabel}</Button>
      </>
    }
  >
    <p style={{ color: 'var(--admin-text-secondary)', fontSize: 14 }}>{message}</p>
  </Modal>
);

// ── Skeleton ──────────────────────────────────────────────
export const Skeleton = ({ width = '100%', height = 14, borderRadius = 6 }) => (
  <div className="admin-skeleton" style={{ width, height, borderRadius }} />
);

export const SkeletonCard = () => (
  <div className="admin-stat-card">
    <Skeleton width="60%" height={12} />
    <div style={{ marginTop: 12 }}>
      <Skeleton width="80%" height={28} />
    </div>
    <div style={{ marginTop: 8 }}>
      <Skeleton width="40%" height={12} />
    </div>
  </div>
);

export const SkeletonTable = ({ rows = 5, cols = 5 }) => (
  <table className="admin-table">
    <thead>
      <tr>{Array.from({ length: cols }).map((_, i) => <th key={i}><Skeleton height={12} /></th>)}</tr>
    </thead>
    <tbody>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: cols }).map((_, j) => <td key={j}><Skeleton height={12} width={j === 0 ? '80%' : '60%'} /></td>)}
        </tr>
      ))}
    </tbody>
  </table>
);

// ── Empty State ───────────────────────────────────────────
export const EmptyState = ({ icon, title = 'No data', message = 'Nothing to display here yet.', action }) => (
  <div className="admin-empty-state">
    <div className="admin-empty-state-icon">{icon || <Search size={28} />}</div>
    <h3>{title}</h3>
    <p>{message}</p>
    {action && <div style={{ marginTop: 16 }}>{action}</div>}
  </div>
);

// ── Alert ─────────────────────────────────────────────────
export const Alert = ({ type = 'info', children }) => {
  const icons = { info: <Info size={16} />, success: <CheckCircle size={16} />, warning: <AlertTriangle size={16} />, danger: <AlertCircle size={16} /> };
  return (
    <div className={`admin-alert admin-alert-${type}`}>
      {icons[type]}
      {children}
    </div>
  );
};

// ── Toggle ────────────────────────────────────────────────
export const Toggle = ({ checked, onChange, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <label className="admin-toggle">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="admin-toggle-slider" />
    </label>
    {label && <span style={{ fontSize: 13, color: 'var(--admin-text-secondary)' }}>{label}</span>}
  </div>
);

// ── Avatar ────────────────────────────────────────────────
export const Avatar = ({ name, size = 34 }) => {
  const initials = name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'NA';
  return (
    <div
      className="admin-table-avatar"
      style={{ width: size, height: size, fontSize: size * 0.35, borderRadius: size * 0.24 }}
    >
      {initials}
    </div>
  );
};

// ── Progress Bar ──────────────────────────────────────────
export const ProgressBar = ({ value, max = 100, color = '#E85D04', height = 8 }) => {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div className="admin-progress-bar" style={{ height }}>
      <div className="admin-progress-fill" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
};

// ── Tabs ──────────────────────────────────────────────────
export const Tabs = ({ tabs, active, onChange }) => (
  <div className="admin-tabs">
    {tabs.map(tab => (
      <button
        key={tab.key}
        className={`admin-tab ${active === tab.key ? 'active' : ''}`}
        onClick={() => onChange(tab.key)}
      >
        {tab.label}
        {tab.count !== undefined && (
          <span style={{
            marginLeft: 6, background: active === tab.key ? 'var(--admin-primary)' : 'var(--admin-border)',
            color: active === tab.key ? 'white' : 'var(--admin-text-secondary)',
            borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700,
          }}>{tab.count}</span>
        )}
      </button>
    ))}
  </div>
);

// ── Loading Overlay ───────────────────────────────────────
export const LoadingOverlay = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
    <div className="admin-spinner admin-spinner-dark" style={{ width: 32, height: 32, borderWidth: 3 }} />
  </div>
);

// ── Info Row ──────────────────────────────────────────────
export const InfoRow = ({ label, value }) => (
  <div className="admin-info-row">
    <span className="admin-info-row-label">{label}</span>
    <span className="admin-info-row-value">{value ?? '—'}</span>
  </div>
);
